import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Map } from 'lucide-react';
import { TOUR_STEPS, THEMES } from '../../utils/constants';

export const GuidedTour = ({ onComplete, activeTab, setActiveTab }) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState(null); // Position of the target element
    const step = TOUR_STEPS[stepIndex];
    
    // --- EFFECT: Handle Navigation & Positioning ---
    useEffect(() => {
        let attempts = 0;
        
        // 1. Switch Tab if needed
        if (step.tab && step.tab !== activeTab) {
            setActiveTab(step.tab);
        }

        // 2. Find Target Element (with retry logic for rendering delays)
        const findTarget = () => {
            // If no target (e.g. Welcome screen), clear rect (center modal)
            if (!step.targetId) {
                setRect(null);
                return;
            }

            const el = document.getElementById(step.targetId);
            if (el) {
                // --- FIX: SCROLL INTO VIEW ---
                // Ensure the element is visible before calculating its position
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Wait a tiny bit for scroll to settle before grabbing coordinates
                setTimeout(() => {
                    const r = el.getBoundingClientRect();
                    // Add padding to the highlight box
                    setRect({
                        top: r.top - 10,
                        left: r.left - 10,
                        width: r.width + 20,
                        height: r.height + 20,
                        bottom: r.bottom + 10 // Store bottom for calculations
                    });
                }, 350); // Delay allows scroll animation to finish
                
            } else if (attempts < 5) {
                // Element not found yet? Wait a bit (React rendering)
                attempts++;
                setTimeout(findTarget, 200);
            } else {
                // Fallback: If element not found after retries, just show centered
                setRect(null);
            }
        };

        // Trigger immediately
        findTarget();

        // Also update on window resize
        window.addEventListener('resize', findTarget);
        return () => window.removeEventListener('resize', findTarget);

    }, [stepIndex, step, activeTab, setActiveTab]);

    // --- ACTIONS ---
    const handleNext = () => {
        if (stepIndex < TOUR_STEPS.length - 1) {
            setStepIndex(stepIndex + 1);
        } else {
            onComplete();
        }
    };

    const handlePrev = () => {
        if (stepIndex > 0) {
            setStepIndex(stepIndex - 1);
        }
    };

    // Determine current theme color based on the step's tab
    const currentElement = step.tab ? (THEMES[step.tab] ? step.tab : 'air') : 'slate';
    const accentColor = THEMES[currentElement]?.hex || '#ffffff';

    // --- SMART POSITIONING LOGIC ---
    // If the target is in the bottom half of the screen, show text ABOVE. Otherwise BELOW.
    const isTargetLow = rect ? rect.top > window.innerHeight / 2 : false;
    
    // Default Style (Centered)
    let cardStyle = { top: '50%', transform: 'translateY(-50%)' };
    
    if (rect) {
        if (isTargetLow) {
            // Position ABOVE the target
            cardStyle = { 
                bottom: window.innerHeight - rect.top + 20,
                top: 'auto',
                transform: 'none'
            };
        } else {
            // Position BELOW the target
            cardStyle = { 
                top: rect.bottom + 20,
                bottom: 'auto',
                transform: 'none'
            };
        }
    }

    // --- RENDER ---
    return (
        <div className="fixed inset-0 z-[100] overflow-hidden pointer-events-auto">
            
            {/* 1. SPOTLIGHT EFFECT (If Target Found) */}
            {rect ? (
                <div 
                    className="absolute transition-all duration-500 ease-in-out border-2 rounded-xl shadow-[0_0_0_9999px_rgba(2,6,23,0.85)] animate-pulse-slow"
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        borderColor: accentColor,
                        boxShadow: `0 0 0 9999px rgba(2,6,23,0.85), 0 0 20px ${accentColor}40` // Dark overlay + Glow
                    }}
                >
                    {/* Pulsing Dot anchor */}
                    <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white rounded-full animate-ping"></div>
                    <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white rounded-full"></div>
                </div>
            ) : (
                // If no target (Welcome Screen), just a dark background
                <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm"></div>
            )}

            {/* 2. THE TOOLTIP CARD */}
            <div 
                className="absolute transition-all duration-500 flex justify-center w-full px-4"
                style={cardStyle}
            >
                <div className="bg-slate-900 border border-slate-700 p-6 rounded-2xl shadow-2xl max-w-md w-full relative animate-fade-in-up">
                    
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-slate-800 rounded-lg">
                                <Map className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white text-lg">{step.title}</h3>
                                <p className="text-[10px] text-slate-400 uppercase tracking-widest">
                                    Step {stepIndex + 1} of {TOUR_STEPS.length}
                                </p>
                            </div>
                        </div>
                        <button onClick={onComplete} className="text-slate-500 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <p className="text-sm text-slate-300 mb-6 leading-relaxed">
                        {step.content}
                    </p>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 h-1 rounded-full mb-6 overflow-hidden">
                        <div 
                            className="h-full transition-all duration-300"
                            style={{ 
                                width: `${((stepIndex + 1) / TOUR_STEPS.length) * 100}%`,
                                backgroundColor: accentColor
                            }}
                        ></div>
                    </div>

                    {/* Controls */}
                    <div className="flex justify-between items-center">
                        <button 
                            onClick={handlePrev} 
                            disabled={stepIndex === 0}
                            className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded transition-colors ${stepIndex === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                        >
                            <ChevronLeft className="w-3 h-3" /> Back
                        </button>

                        <button 
                            onClick={handleNext}
                            className="flex items-center gap-2 text-slate-900 font-bold px-6 py-2 rounded-lg text-sm hover:scale-105 transition-transform"
                            style={{ backgroundColor: accentColor }}
                        >
                            {stepIndex === TOUR_STEPS.length - 1 ? "Finish Tour" : "Next"} 
                            {stepIndex < TOUR_STEPS.length - 1 && <ChevronRight className="w-3 h-3" />}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};